// Client-only UTM capture. Reads utm_* + gclid/fbclid from the current URL,
// merges them with any values persisted from earlier landings in the same
// session (sessionStorage), and returns a flat payload safe to spread into
// any `submitLead({ data: ... })` call.
//
// A lead form is rarely on the landing page — the visitor clicks around
// before converting. Persisting the first-touch UTMs lets attribution
// survive that navigation without needing router-level plumbing.

import { captureAiReferrer, getAiReferral } from "./ai-referrer";

const KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
type UtmKey = (typeof KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "sam.utm.v1";

function readStored(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as UtmParams;
    return isPlainRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(v: UtmParams) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // sessionStorage disabled — fine, fall back to per-page URL parsing.
  }
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function fromUrl(): UtmParams {
  if (typeof window === "undefined") return {};
  const out: UtmParams = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    for (const k of KEYS) {
      const v = sp.get(k);
      if (v && v.trim().length > 0) out[k] = v.trim().slice(0, 120);
    }
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Capture UTMs from the current URL and merge them into sessionStorage.
 * Safe to call on every route load — first-touch wins.
 *
 * When the visit carries no campaign tags but arrived from an external AI
 * assistant (ChatGPT, Perplexity, Claude ...), the referrer host is recorded
 * as the source so answer-engine traffic is attributed rather than dumped
 * into "direct". Real UTM tags always win over the inferred value.
 */
export function captureUtmParams(): UtmParams {
  const stored = readStored();
  const url = fromUrl();
  const merged: UtmParams = { ...stored };
  for (const k of KEYS) {
    // First-touch wins: only fill a slot that isn't already stored.
    if (!merged[k] && url[k]) merged[k] = url[k];
  }
  let touchedAi = false;
  if (!merged.utm_source) {
    const ai = captureAiReferrer();
    if (ai) {
      merged.utm_source = ai;
      merged.utm_medium = merged.utm_medium || "ai_referral";
      touchedAi = true;
    }
  }
  if (Object.keys(url).length > 0 || touchedAi) writeStored(merged);
  return merged;
}

/** Return the current UTM snapshot without mutating storage. */
export function getUtmParams(): UtmParams {
  const stored = readStored();
  const url = fromUrl();
  const merged: UtmParams = { ...url, ...stored };
  if (!merged.utm_source) {
    const ai = getAiReferral();
    if (ai) {
      merged.utm_source = ai.utm_source;
      merged.utm_medium = merged.utm_medium || ai.utm_medium;
    }
  }
  return merged;
}

